import React from 'react';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CropFreeIcon from '@mui/icons-material/CropFree';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useTranslation} from 'react-i18next';
import {Panel} from 'reactflow';

const CONTROL_SIZE = 36;

interface EditorControlsPanelProps {
    onZoomIn: () => void;
    onZoomOut: () => void;
    onResetView: () => void;
    onAutoLayout: () => void;
    autoLayoutDisabled?: boolean;
    agentOpen: boolean;
    onOpenAgent: () => void;
    /** False hides the agent entry point entirely - see useAutomationAgentEnabled. */
    agentEnabled: boolean;
}

const EditorControlsPanel: React.FC<EditorControlsPanelProps> = ({
    onZoomIn,
    onZoomOut,
    onResetView,
    onAutoLayout,
    autoLayoutDisabled = false,
    agentOpen,
    onOpenAgent,
    agentEnabled
}) => {
    const {t} = useTranslation();

    return (
        <Panel position={'bottom-left'}>
            <Paper
                variant={'outlined'}
                sx={{
                    overflow: 'hidden',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    borderColor: 'divider'
                }}
            >
                <Stack direction={'row'} alignItems={'center'}>
                    <Tooltip title={t('Zoom out')} placement={'top'}>
                        <IconButton
                            size={'small'}
                            aria-label={t('Zoom out')}
                            onClick={onZoomOut}
                            sx={{width: CONTROL_SIZE, height: CONTROL_SIZE}}
                        >
                            <ZoomOutIcon/>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={t('Zoom in')} placement={'top'}>
                        <IconButton
                            size={'small'}
                            aria-label={t('Zoom in')}
                            onClick={onZoomIn}
                            sx={{width: CONTROL_SIZE, height: CONTROL_SIZE}}
                        >
                            <ZoomInIcon/>
                        </IconButton>
                    </Tooltip>

                    <Divider orientation={'vertical'} flexItem sx={{mx: 0.5, my: 0.75}}/>

                    <Tooltip title={t('Reset view')} placement={'top'}>
                        <IconButton
                            size={'small'}
                            aria-label={t('Reset view')}
                            onClick={onResetView}
                            sx={{width: CONTROL_SIZE, height: CONTROL_SIZE}}
                        >
                            <CropFreeIcon/>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={t('Fix layout')} placement={'top'}>
                        <span>
                            <IconButton
                                size={'small'}
                                aria-label={t('Fix layout')}
                                disabled={autoLayoutDisabled}
                                onClick={onAutoLayout}
                                sx={{width: CONTROL_SIZE, height: CONTROL_SIZE}}
                            >
                                <AutoFixHighIcon/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    {agentEnabled && !agentOpen && (
                        <Button color="error" size="small" onClick={onOpenAgent} endIcon={<ChevronRightIcon/>}
                                aria-controls="automation-agent-panel"
                                sx={{height: CONTROL_SIZE, px: 1.25, textTransform: 'none', whiteSpace: 'nowrap'}}>
                            {t('Show agent')}
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Panel>
    );
};

export default EditorControlsPanel;
